import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const LOGIN_BG = "https://images.unsplash.com/photo-1776090188275-72957bae4ed7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwZW5naW5lZXJpbmclMjBmYWN0b3J5JTIwaW50ZXJpb3IlMjBibHVyfGVufDB8fHx8MTc3ODY2MTQxN3ww&ixlib=rb-4.1.0&q=85";

function formatErr(detail) {
    if (!detail) return "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    if (detail.msg) return detail.msg;
    return String(detail);
}

export default function Login() {
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !password) { toast.error("Email and password required"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/auth/login", { email: email.toLowerCase().trim(), password });
            setUser(data);
            if (data.must_change_password) {
                toast.success("Welcome — please set a new password.");
                navigate("/change-password?required=1");
            } else {
                navigate("/dashboard");
            }
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail) || "Login failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-white">
            <div className="hidden lg:block relative">
                <img src={LOGIN_BG} alt="Factory" className="absolute inset-0 w-full h-full object-cover grayscale" />
                <div className="absolute inset-0 bg-[#002FA7]/85" />
                <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
                    <div className="flex items-center gap-3">
                        <img src="/macho-logo.png" alt="MACHO" className="h-16 w-auto object-contain bg-white p-1" />
                        <div className="text-[11px] uppercase tracking-[0.2em] font-mono opacity-80">Sales Champ</div>
                    </div>
                    <div>
                        <h1 className="text-5xl font-bold tracking-tight leading-[1.05]">
                            Engineering<br />precision in<br />every cost line.
                        </h1>
                        <p className="mt-6 text-white/80 max-w-md text-sm">
                            Master your machine hour rate, BOM, manpower and overhead in one Swiss-clean control room.
                        </p>
                        <div className="brand-stripe mt-8 w-48" />
                    </div>
                    <div className="text-xs text-white/60 font-mono">© MACHO · Sales Champ · v1.0</div>
                </div>
            </div>

            <div className="flex items-center justify-center p-8">
                <form onSubmit={submit} className="w-full max-w-md">
                    <div className="tag-pill mb-6">Sign in</div>
                    <h2 className="text-3xl font-bold tracking-tight mb-2">Welcome back</h2>
                    <p className="text-sm text-zinc-500 mb-8">
                        Sign in with your work email and password.
                    </p>

                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Email</Label>
                            <Input
                                type="email" autoComplete="email" required
                                value={email} onChange={(e) => setEmail(e.target.value)}
                                className="h-11 rounded-none"
                                data-testid="login-email"
                                placeholder="you@company.com"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                                <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Password</Label>
                                <Link to="/forgot-password" className="text-[11px] uppercase tracking-wider text-[#002FA7] hover:underline" data-testid="forgot-password-link">Forgot?</Link>
                            </div>
                            <Input
                                type="password" autoComplete="current-password" required
                                value={password} onChange={(e) => setPassword(e.target.value)}
                                className="h-11 rounded-none"
                                data-testid="login-password"
                            />
                        </div>
                    </div>

                    <Button
                        type="submit" disabled={busy}
                        className="w-full mt-6 rounded-none h-12 bg-[#002FA7] hover:bg-[#00227B] text-white font-medium"
                        data-testid="login-submit"
                    >
                        {busy ? "Signing in…" : "Sign in"}
                    </Button>

                    <p className="mt-6 text-xs text-zinc-500 font-mono">
                        New here? Your administrator will share an activation link by email.
                    </p>
                </form>
            </div>
        </div>
    );
}
